import { IStateContextData } from '../interfaces/state.interfaces';
import { calculateBMI, kgToLbs, lbsToKg } from './forms.helpers';
import { getCmFromHeightObj } from './height.helpers';

export const getCurrentWeightBMI = (
  currentWeight: IStateContextData['currentWeight'],
  currentHeight: IStateContextData['height'],
): number => {
  const weight = getKgFromWeightObj(currentWeight);
  const height = getCmFromHeightObj(currentHeight);

  return calculateBMI(height, weight);
};

export const getTargetWeightLose = (
  currentWeight: IStateContextData['currentWeight'],
  targetWeight: IStateContextData['targetWeight'],
): number | undefined => {
  const target = getKgFromWeightObj(targetWeight);
  const current = getKgFromWeightObj(currentWeight);

  if (!target || !current) return;

  const result = ((current - target) / current) * 100;
  return result > 1 ? Number(result.toFixed(1)) : undefined;
};

export const getWeightWithMeasurement = (
  weight:
    IStateContextData['currentWeight'] | IStateContextData['targetWeight'],
) => {
  return weight?.isMetric ? `${weight.kg} kg` : `${weight?.lbs} lbs`;
};

export const getKgFromWeightObj = (
  data: IStateContextData['currentWeight'] | IStateContextData['targetWeight'],
): number => {
  if (!data?.isMetric) return lbsToKg(data?.lbs);
  return data?.kg || 0;
};

export const getCorrectWeightByMeasurement = (
  data: IStateContextData['currentWeight'] | IStateContextData['targetWeight'],
  isMetric: boolean,
): number => {
  if (isMetric) {
    return Number(getKgFromWeightObj(data));
  }

  return Number(data?.isMetric ? kgToLbs(data?.kg) : data?.lbs);
};

export const getWeightGraphParams = (
  currentWeight: IStateContextData['currentWeight'],
  targetWeight: IStateContextData['targetWeight'],
): string[] => {
  const isMetric = !!targetWeight?.isMetric;

  const start = getCorrectWeightByMeasurement(currentWeight, isMetric);
  const end = getCorrectWeightByMeasurement(targetWeight, isMetric);
  const middle = (start + end) / 2;

  return [
    getWeightWithMeasurement({ isMetric, lbs: start, kg: start }),
    getWeightWithMeasurement({ isMetric, lbs: middle, kg: middle }),
    getWeightWithMeasurement({ isMetric, lbs: end, kg: end }),
  ];
};

export const determineWeightLossRate = (bmi: number): number | undefined => {
  if (bmi < 25) return 0.5;
  if (bmi >= 25 && bmi < 30) return 1;
  if (bmi >= 30) return 1.5;
};

export const calculateWeeks = (
  rate: number,
  currentWeight: number,
  targetWeight: number,
): number => {
  const totalWeightGoal = Number(
    Number(currentWeight) - Number(targetWeight) - 2,
  );

  return Math.ceil(Math.max(1 + totalWeightGoal, 1) / Number(rate));
};
